import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Format a date as mm-dd-yyyy (US, zero-padded). Standard display format across the app.
 * Accepts a Date, an ISO string, or a yyyy-mm-dd string. Returns "" for empty/invalid input.
 */
export function fmtDate(value) {
  if (value === null || value === undefined || value === "") return "";
  let d;
  if (value instanceof Date) {
    d = value;
  } else {
    const s = String(value);
    // Plain yyyy-mm-dd (no time) → parse as local date to avoid TZ shifting the day.
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[2]}-${m[3]}-${m[1]}`;
    d = new Date(s);
  }
  if (isNaN(d.getTime())) return String(value);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

/** Format a US phone number as (xxx) xxx-xxxx. Passes through non-10-digit values. */
export function fmtPhone(value) {
  if (value === null || value === undefined || value === "") return "";
  let d = String(value).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(value);
}

/** Format a date-time as mm-dd-yyyy h:mm AM/PM. Returns "" for empty/invalid input. */
export function fmtDateTime(value) {
  if (value === null || value === undefined || value === "") return "";
  const d = value instanceof Date ? value : new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  const date = fmtDate(d);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date} ${time}`;
}

/**
 * Medication-safety display fix: a dose value with a bare leading decimal (".5 mg")
 * always gets a leading zero ("0.5 mg") — a missed decimal point turns ".5" into "5.".
 * Display-only; does not touch stored dose values, matching, or the med catalog.
 * Values that already look correct (5 mg, 2.5 mg, 0.5 mg) pass through unchanged.
 */
export function formatDose(value) {
  if (value === null || value === undefined) return value;
  return String(value).replace(/(^|[^0-9])\.(\d)/g, "$10.$2");
}

/** Threshold past which an active off-site trip is flagged as overdue on the tickers below. */
export const OFFSITE_OVERDUE_HOURS = 4;

/**
 * How long an off-site trip has been active, for the "OFF-SITE" tickers.
 * Returns null if sentDate is missing/invalid. `overdue` flips true past OFFSITE_OVERDUE_HOURS.
 */
export function offsiteDuration(sentDate) {
  if (!sentDate) return null;
  const s = new Date(String(sentDate).length <= 10 ? `${sentDate}T00:00:00` : sentDate);
  if (isNaN(s.getTime())) return null;
  const ms = Date.now() - s.getTime();
  if (ms < 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, overdue: ms > OFFSITE_OVERDUE_HOURS * 3600000 };
}

// Display name including the middle name when one is on file.
export function inmateName(i) {
    if (!i) return "";
    return [i.first_name, i.middle_name, i.last_name].filter(Boolean).join(" ").trim();
}
